'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import {
	VaultAccount,
	Vault,
	VAULT_PROGRAM_ID,
	getDriftVaultProgram,
	VaultDepositor,
	VaultDepositorAccount,
} from '@velocity-exchange/vaults-sdk';
import {
	isValidPublicKey,
	createPlaceholderIWallet,
} from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { PublicKey } from '@velocity-exchange/sdk';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { useQueryClient } from '@tanstack/react-query';
import { vaultQueryKeys } from 'src/hooks/query/vaults/vaultQueryKeys';
import { VaultsOnChainDataLookup } from 'src/hooks/vaults/vaultStatsSyncUtils';
import { VaultDepositorAccountsLookup } from 'src/hooks/query/vaults/types';

type VaultAccountContextType = {
	vaultPubkey: string;
};

const VaultAccountContext = createContext<VaultAccountContextType | null>(null);

/**
 * Subscribes to vault and depositor account updates for a specific vault.
 * Updates React Query cache when subscription events fire.
 */
export const SubscribedVaultProvider = (props: {
	children: React.ReactNode;
	vaultPubkey: string;
}) => {
	const { children, vaultPubkey } = props;
	const queryClient = useQueryClient();
	const authority = useCurrentAuthority();
	const connection = useVelocityStore((s) => s.connection.current);
	const bulkAccountLoader = useVelocityStore((s) => s.connection.accountLoader);

	const vaultAccountRef = useRef<VaultAccount | undefined>(undefined);
	const vaultUpdateHandlerRef = useRef<((data: Vault) => void) | undefined>(
		undefined
	);
	const vaultDepositorAccountRef = useRef<VaultDepositorAccount | undefined>(
		undefined
	);
	const vaultDepositorUpdateHandlerRef = useRef<
		((data: VaultDepositor) => void) | undefined
	>(undefined);

	// Subscribe to vault account updates
	useEffect(() => {
		// Skip subscription for external vaults with non-base58 identifiers
		if (!vaultPubkey || !connection || !bulkAccountLoader) return;
		if (!isValidPublicKey(vaultPubkey)) return;

		let cancelled = false;

		async function subscribeToVault() {
			if (!connection || !bulkAccountLoader || !vaultPubkey) return;

			const newWallet = createPlaceholderIWallet();
			const velocityVaultsProgram = getDriftVaultProgram(connection, newWallet);

			const vaultAccount = new VaultAccount(
				velocityVaultsProgram,
				new PublicKey(vaultPubkey),
				bulkAccountLoader
			);

			await vaultAccount.subscribe();

			// The account subscriber only honours `unsubscribe` once it has finished
			// subscribing, so a cleanup that landed mid-subscribe was a no-op and this
			// account is ours to tear down. Publishing it instead would leave it polling
			// forever and would clobber the newer run's handler ref, aiming the eventual
			// `off()` at the wrong handler.
			if (cancelled) {
				vaultAccount.unsubscribe();
				return;
			}

			vaultAccountRef.current = vaultAccount;

			const vaultAccountData = vaultAccount.getData();

			// Update React Query cache with initial data
			queryClient.setQueryData<VaultsOnChainDataLookup>(
				vaultQueryKeys.vaultOnChainData(),
				(prev) => {
					if (!prev) return prev;
					return {
						...prev,
						[vaultPubkey]: {
							...prev[vaultPubkey],
							vaultAccountData,
						},
					};
				}
			);

			const vaultUpdateHandler = (newVaultAccountData: Vault) => {
				// Merge into React Query cache
				queryClient.setQueryData<VaultsOnChainDataLookup>(
					vaultQueryKeys.vaultOnChainData(),
					(prev) => {
						if (!prev) return prev;
						return {
							...prev,
							[vaultPubkey]: {
								...prev[vaultPubkey],
								vaultAccountData: newVaultAccountData,
							},
						};
					}
				);
			};

			vaultUpdateHandlerRef.current = vaultUpdateHandler;
			vaultAccount.eventEmitter.on('vaultUpdate', vaultUpdateHandler);
		}

		subscribeToVault();

		return () => {
			cancelled = true;

			if (vaultAccountRef.current && vaultUpdateHandlerRef.current) {
				vaultAccountRef.current.eventEmitter.off(
					'vaultUpdate',
					vaultUpdateHandlerRef.current
				);
			}
			vaultUpdateHandlerRef.current = undefined;
			vaultAccountRef.current?.unsubscribe();
			vaultAccountRef.current = undefined;
		};
	}, [vaultPubkey, connection, bulkAccountLoader, queryClient]);

	// Subscribe to vault depositor account updates
	useEffect(() => {
		// Skip subscription for external vaults with non-base58 identifiers
		if (!vaultPubkey || !authority || !bulkAccountLoader || !connection) {
			return;
		}
		if (!isValidPublicKey(vaultPubkey)) return;

		let cancelled = false;

		async function subscribeToVaultDepositor() {
			if (!connection || !bulkAccountLoader || !authority || !vaultPubkey)
				return;

			const vaultDepositorPubkey = VaultDepositorAccount.getAddressSync(
				VAULT_PROGRAM_ID,
				new PublicKey(vaultPubkey),
				authority
			);
			const newWallet = createPlaceholderIWallet(vaultDepositorPubkey);
			const velocityVaultsProgram = getDriftVaultProgram(connection, newWallet);

			const vaultDepositorAccount = new VaultDepositorAccount(
				velocityVaultsProgram,
				vaultDepositorPubkey,
				bulkAccountLoader
			);
			await vaultDepositorAccount.subscribe();

			// See the vault-account effect above: a cleanup during `subscribe` cannot
			// tear this down, so the superseded run has to do it itself.
			if (cancelled) {
				vaultDepositorAccount.unsubscribe();
				return;
			}

			vaultDepositorAccountRef.current = vaultDepositorAccount;

			let vaultDepositorAccountData: VaultDepositor | undefined;
			try {
				vaultDepositorAccountData = vaultDepositorAccount.getData();
			} catch {
				// error means vault depositor is not initialized
			}

			// Update React Query cache with initial data if depositor exists
			if (vaultDepositorAccountData) {
				const vaultAccountData = vaultAccountRef.current?.getData();
				if (vaultAccountData) {
					queryClient.setQueryData<VaultDepositorAccountsLookup>(
						vaultQueryKeys.vaultDepositorAccounts(authority.toString()),
						(prev) => {
							return {
								...prev,
								[vaultPubkey]: {
									vaultDepositor: vaultDepositorAccountData,
									vault: vaultAccountData,
								},
							};
						}
					);
				}
			}

			const vaultDepositorUpdateHandler = (
				newVaultDepositorData: VaultDepositor
			) => {
				// Merge into React Query cache
				const vaultAccountData = vaultAccountRef.current?.getData();
				if (vaultAccountData) {
					queryClient.setQueryData<VaultDepositorAccountsLookup>(
						vaultQueryKeys.vaultDepositorAccounts(authority.toString()),
						(prev) => {
							return {
								...prev,
								[vaultPubkey]: {
									vaultDepositor: newVaultDepositorData,
									vault: vaultAccountData,
								},
							};
						}
					);
				}
			};

			vaultDepositorUpdateHandlerRef.current = vaultDepositorUpdateHandler;
			vaultDepositorAccount.eventEmitter.on(
				'vaultDepositorUpdate',
				vaultDepositorUpdateHandler
			);
		}

		subscribeToVaultDepositor();

		return () => {
			cancelled = true;

			if (
				vaultDepositorAccountRef.current &&
				vaultDepositorUpdateHandlerRef.current
			) {
				vaultDepositorAccountRef.current.eventEmitter.off(
					'vaultDepositorUpdate',
					vaultDepositorUpdateHandlerRef.current
				);
			}
			vaultDepositorUpdateHandlerRef.current = undefined;
			vaultDepositorAccountRef.current?.unsubscribe();
			vaultDepositorAccountRef.current = undefined;
		};
	}, [vaultPubkey, authority, bulkAccountLoader, connection, queryClient]);

	return (
		<VaultAccountContext
			value={{
				vaultPubkey,
			}}
		>
			{children}
		</VaultAccountContext>
	);
};

export const useSubscribedVaultContext = () => {
	const context = useContext(VaultAccountContext);

	if (!context) {
		throw new Error(
			'useSubscribedVaultContext must be used within a SubscribedVaultProvider'
		);
	}

	return context;
};
